import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  Menu,
  Lock,
  Compass,
  ClipboardCheck,
  ShieldCheck,
  Award,
  FileText,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import { PortalHeader, useLearner } from "./PortalDashboard";
import { previewStore } from "./previewStore";
import PreviewBanner from "./PreviewBanner";
import api from "../../lib/api";
import fd2gApi, { getFd2gApiMode } from "./fd2gApi";
import usePageMeta from "../../lib/usePageMeta";

// ---------------------------------------------------------------------------
// Small display-safety helper. Decodes numeric HTML entities (&#x41; / &#65;)
// back into their characters. Data in fd2g_fac_001.py contains ZERO such
// entities today — but a middleware or browser layer may inject them (some
// content-obfuscation / anti-scrape features). This decode is a no-op for
// entity-free strings and never modifies real & characters that are not
// part of a numeric entity, so it is safe to apply to any user-visible text
// or option label.
// ---------------------------------------------------------------------------
function decodeDisplayEntities(input) {
  if (typeof input !== "string") return input;
  if (input.indexOf("&#") === -1) return input;
  return input
    .replace(/&#x([0-9A-Fa-f]+);/g, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(parseInt(n, 10)));
}

const COURSE_CODE = "FD2G-FAC-001";

// ---------------------------------------------------------------------------
// FD2G-FAC-001 — The Five Voices of the Journey · Certified Facilitator Training
// Phase 2 renderer. Welcome & Orientation and Session 1 (NOTICE) are fully
// populated with instructional content and a scored 10-question assessment.
// Sessions 2-5, Code of Ethics, Facilitator Agreement, Final Assessment, and
// Credential remain shell placeholders — walkable to preview the progression.
// ---------------------------------------------------------------------------

function sectionKindLabel(sess) {
  const k = sess?.section_kind || "instructional";
  if (k === "orientation") return "Orientation";
  if (k === "instructional") return "Voice";
  if (k === "policy_ack") return "Required Acknowledgement";
  if (k === "final_assessment") return "Certification Assessment";
  if (k === "credential") return "Credential";
  return "Section";
}
function sectionIcon(kind) {
  if (kind === "orientation") return Compass;
  if (kind === "policy_ack") return ShieldCheck;
  if (kind === "final_assessment") return ClipboardCheck;
  if (kind === "credential") return Award;
  return FileText;
}

export default function PortalFd2gFacLearn() {
  usePageMeta({
    title: "FD2G-FAC-001 — The Five Voices of the Journey | CCCHAPS Academy Preview",
    noindex: true,
  });

  const inPreview = previewStore.isActive() && previewStore.getCourseCode() === COURSE_CODE;
  const learner = useLearner();
  const nav = useNavigate();
  const [apiMode] = useState(() => getFd2gApiMode()); // "learner" | "admin"
  const fd2g = useMemo(() => fd2gApi(), []); // dual-path client, resolved once per mount
  const [course, setCourse] = useState(null);
  const [pos, setPos] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const [activeSessionOrder, setActiveSessionOrder] = useState(0);

  // Server-truth completion state
  const [completionState, setCompletionState] = useState(null);

  // Orientation ack local state
  const [orientationChecked, setOrientationChecked] = useState({});
  const [orientationSubmitting, setOrientationSubmitting] = useState(false);

  // Session 1 KC state (Practice Contrast) — client-side only.
  const [kcAnswers, setKcAnswers] = useState({}); // { [screenId]: { key, correct } }

  // Document acceptance checkbox state — keyed by document_type on the screen.
  const [docChecked, setDocChecked] = useState({}); // { [document_type]: bool }
  const [docSubmitting, setDocSubmitting] = useState(false);

  // Session 1 assessment state — scoped by session order (nested below).
  const [assessmentAnswersAll, setAssessmentAnswersAll] = useState({}); // { [sessionOrder]: { [qId]: key } }
  const [assessmentResultsAll, setAssessmentResultsAll] = useState({}); // { [sessionOrder]: result }
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false);

  // Final Certification Assessment (Phase 6) state
  const [finalAnswers, setFinalAnswers] = useState({}); // { [questionId]: key }
  const [finalResult, setFinalResult] = useState(null);
  const [finalSubmitting, setFinalSubmitting] = useState(false);

  // Phase 7 — credential issuance
  const [credentialSubmitting, setCredentialSubmitting] = useState(false);

  // View Credential modal — opens an authoritative server-record view.
  // IMPORTANT: this never issues or re-issues a credential. It performs a
  // GET against the credential endpoint and renders the returned record.
  const [viewCredentialOpen, setViewCredentialOpen] = useState(false);
  const [viewCredentialRecord, setViewCredentialRecord] = useState(null);
  const [viewCredentialLoading, setViewCredentialLoading] = useState(false);

  // Issue 1 — Assessment integrity:
  //  * `assessmentAnswersAll` is nested by session order, so switching
  //    sessions never clears state mid-flow. A fresh session reads its
  //    own empty object, so no pre-selected radios bleed in from a prior
  //    pass. The submit payload only contains the current session's keys.
  //  * `assessmentResultsAll` is similarly scoped so a prior session's
  //    result cannot appear on a different session's gate.
  //
  // Derived views for the CURRENT active session:
  const _curSessionOrder = activeSessionOrder;
  const assessmentAnswers = assessmentAnswersAll[_curSessionOrder] || {};
  const assessmentResult = assessmentResultsAll[_curSessionOrder] || null;
  const setAssessmentAnswers = (updater) => {
    setAssessmentAnswersAll((prev) => {
      const curr = prev[_curSessionOrder] || {};
      const next = typeof updater === "function" ? updater(curr) : updater;
      return { ...prev, [_curSessionOrder]: next };
    });
  };
  const setAssessmentResult = (val) => {
    setAssessmentResultsAll((prev) => ({ ...prev, [_curSessionOrder]: val }));
  };

  const refreshCompletionState = async () => {
    try {
      const r = await fd2g.get("completion-state");
      setCompletionState(r.data);
      const checked = r.data?.orientation?.checked;
      if (checked) setOrientationChecked((prev) => ({ ...checked, ...prev }));
    } catch (_e) { /* silent — admin-only */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await fd2g.get("course");
        setCourse(r.data.course);
        refreshCompletionState();
      } catch (err) {
        // 401s are surfaced by the api interceptor. Real learners follow the
        // Academy's normal authentication flow; no admin-specific redirect
        // now that FD2G-FAC-001 is published.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  if (!course) {
    return (
      <div className="min-h-screen bg-[#FDFCF6]">
        <PortalHeader learner={learner} />
        <div className="p-12 text-center text-slate-500">Loading FD2G-FAC-001 preview…</div>
      </div>
    );
  }

  const sessions = [...(course.sessions || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Server-truth gating cascades through the Five Voices, then unlocks the
  // Code of Ethics placeholder once HONOR passes.
  const orientationDone = !!completionState?.orientation?.acknowledged;
  const noticePassed = !!completionState?.sessions?.[1]?.passed;
  const listenPassed = !!completionState?.sessions?.[2]?.passed;
  const understandPassed = !!completionState?.sessions?.[3]?.passed;
  const respondPassed = !!completionState?.sessions?.[4]?.passed;
  const honorPassed = !!completionState?.sessions?.[5]?.passed;
  const coeAccepted = !!completionState?.documents?.code_of_ethics?.accepted;
  const agrAccepted = !!completionState?.documents?.facilitator_agreement?.accepted;
  const finalUnlocked = !!completionState?.final_assessment?.unlocked;
  const finalStatus = completionState?.final_assessment?.status || "NOT_STARTED";
  const finalPassed = finalStatus === "PASSED";
  const finalPending = finalStatus === "PENDING_CERTIFICATION_REVIEW";
  const finalAttemptsUsed = completionState?.final_assessment?.attempts_used ?? 0;
  const finalAttemptsRemaining = completionState?.final_assessment?.attempts_remaining ?? 2;
  const credentialUnlocked = !!completionState?.credential_unlocked;
  const credentialInfo = completionState?.credential || null;
  const credentialIssued = !!credentialInfo?.issued;

  const issueCredential = async () => {
    try {
      setCredentialSubmitting(true);
      const r = await fd2g.post("credential/issue");
      if (r.data?.issued_now) {
        toast.success(`Credential issued: ${r.data.credential.credential_id}`);
      } else {
        toast.success(`Credential is already issued: ${r.data.credential.credential_id}`);
      }
      refreshCompletionState();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not issue credential.";
      toast.error(typeof msg === "string" ? msg : "Could not issue credential.");
    } finally {
      setCredentialSubmitting(false);
    }
  };

  const downloadCertificate = async () => {
    try {
      const r = await fd2g.get("credential/certificate.pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `FD2G-Certified-Facilitator-${credentialInfo?.credential_id || "certificate"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      toast.error("Certificate download failed.");
    }
  };

  // View Credential — read-only fetch of the authoritative server-side
  // credential record (GET /credential). Does NOT issue or alter the
  // credential. Opens a modal that displays the server-returned fields.
  const viewCredential = async () => {
    try {
      setViewCredentialLoading(true);
      setViewCredentialOpen(true);
      const r = await fd2g.get("credential");
      setViewCredentialRecord(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not load credential.");
      setViewCredentialOpen(false);
    } finally {
      setViewCredentialLoading(false);
    }
  };

  const isSessionUnlocked = (sess) => {
    if (sess.section_kind === "orientation") return true;
    if (sess.order === 1) return orientationDone;
    if (sess.order === 2) return noticePassed;
    if (sess.order === 3) return listenPassed;
    if (sess.order === 4) return understandPassed;
    if (sess.order === 5) return respondPassed;
    if (sess.order === 6) return honorPassed; // Code of Ethics
    if (sess.order === 7) return honorPassed && coeAccepted; // Facilitator Agreement
    if (sess.order === 8) return finalUnlocked; // Final Certification Assessment
    if (sess.order === 9) return credentialUnlocked; // Credential (admin preview only)
    return true;
  };
  const availableSessions = sessions.filter((s) => s.status === "available");

  const activeSession = sessions.find((s) => s.order === activeSessionOrder) || sessions[0];
  const screens = activeSession?.screens || [];
  const totalSteps = 1 + screens.length; // [overview, ...screens]
  const currentIsOverview = pos === 0;
  const currentScreen = pos > 0 ? screens[pos - 1] : null;

  const previewState = previewStore.getState();
  const completions = previewState.progress?.section_completions || {};
  const screenDone = (id) => !!completions[`fd2gfac::${id}`];
  const markScreenDone = (id) => {
    if (id) previewStore.markSectionComplete(`fd2gfac::${id}`);
  };

  const doneCount = screens.filter((sc) => screenDone(sc.id)).length;
  const pct = screens.length ? Math.round((doneCount * 100) / screens.length) : 0;

  const allWalkableScreens = availableSessions.flatMap((s) => s.screens || []);
  const globalDoneCount = allWalkableScreens.filter((sc) => screenDone(sc.id)).length;
  const globalPct = allWalkableScreens.length ? Math.round((globalDoneCount * 100) / allWalkableScreens.length) : 0;

  const nextSection = availableSessions.find((s) => (s.order ?? 0) > (activeSessionOrder ?? 0));
  const prevSection = [...availableSessions].reverse().find((s) => (s.order ?? 0) < (activeSessionOrder ?? 0));

  // ---- Gate: can-advance from the current step ----
  const currentCanAdvance = (() => {
    if (currentIsOverview) return true;
    if (!currentScreen) return true;
    // Orientation ack — must have all four required checkboxes true (and persisted).
    if (currentScreen.kind === "orientation_ack") {
      return orientationDone;
    }
    // Knowledge check (Practice Contrast) — must pick an answer to continue.
    if (currentScreen.kind === "knowledge_check") {
      return !!kcAnswers[currentScreen.id];
    }
    // Assessment question — must select an option to continue.
    if (currentScreen.kind === "assessment_question") {
      return !!assessmentAnswers[currentScreen.question_id];
    }
    // Assessment gate — must have submitted and passed to leave NOTICE.
    if (currentScreen.kind === "assessment_gate") {
      return !!(assessmentResult && assessmentResult.passed);
    }
    // Final assessment question — must select an option to continue.
    if (currentScreen.kind === "final_assessment_question") {
      return !!finalAnswers[currentScreen.question_id];
    }
    // Final assessment submit gate — must PASS the assessment to leave section.
    if (currentScreen.kind === "final_assessment_gate") {
      return finalPassed;
    }
    // Document acceptance — server-truth acceptance required to advance.
    if (currentScreen.kind === "document_acceptance") {
      const dtype = currentScreen.document_type;
      if (dtype === "CODE_OF_ETHICS") return coeAccepted;
      if (dtype === "FACILITATOR_AGREEMENT") return agrAccepted;
      return false;
    }
    return true;
  })();

  // Server-side orientation submit
  const submitOrientation = async () => {
    // Local guard: all 4 required.
    const required = (currentScreen?.acknowledgements || []).map((a) => a.id);
    const missing = required.filter((id) => !orientationChecked[id]);
    if (missing.length > 0) {
      toast.error("All four acknowledgements are required to complete orientation.");
      return;
    }
    try {
      setOrientationSubmitting(true);
      await fd2g.post("orientation/acknowledge", {
        acknowledgements: Object.fromEntries(required.map((id) => [id, true])),
      });
      toast.success("Orientation complete. Session One — NOTICE is now unlocked.");
      markScreenDone(currentScreen.id);
      refreshCompletionState();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save orientation.";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setOrientationSubmitting(false);
    }
  };

  const submitAssessment = async () => {
    // Guard: all questions answered
    const questionScreens = (activeSession?.screens || []).filter((s) => s.kind === "assessment_question");
    const missing = questionScreens.filter((s) => !assessmentAnswers[s.question_id]);
    if (missing.length > 0) {
      toast.error(`Please answer all ${questionScreens.length} questions before submitting. Missing: ${missing.length}.`);
      return;
    }
    try {
      setAssessmentSubmitting(true);
      const r = await fd2g.post("session-assessment/submit", {
        session_order: activeSession.order,
        answers: assessmentAnswers,
      });
      setAssessmentResult(r.data);
      if (r.data.passed) {
        toast.success(`Passed — ${r.data.percent}% (${r.data.score}/${r.data.total}). NOTICE completed.`);
      } else {
        toast.error(`Score ${r.data.percent}% (${r.data.score}/${r.data.total}). 80% required. Retake permitted.`);
      }
      refreshCompletionState();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Assessment submission failed.";
      toast.error(typeof msg === "string" ? msg : "Assessment submission failed.");
    } finally {
      setAssessmentSubmitting(false);
    }
  };

  const retakeAssessment = () => {
    // Only clear the CURRENT session's answers/result. Other sessions'
    // results remain intact.
    setAssessmentAnswersAll((prev) => ({ ...prev, [_curSessionOrder]: {} }));
    setAssessmentResultsAll((prev) => ({ ...prev, [_curSessionOrder]: null }));
    const firstQ = (activeSession?.screens || []).findIndex((s) => s.kind === "assessment_question");
    if (firstQ >= 0) setPos(firstQ + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitFinalAssessment = async () => {
    const finalSection = sessions.find((s) => s.section_kind === "final_assessment");
    const qScreens = (finalSection?.screens || []).filter((s) => s.kind === "final_assessment_question");
    const missing = qScreens.filter((s) => !finalAnswers[s.question_id]);
    if (missing.length > 0) {
      toast.error(`Please answer all ${qScreens.length} questions before submitting. Missing: ${missing.length}.`);
      return;
    }
    try {
      setFinalSubmitting(true);
      const r = await fd2g.post("final-assessment/submit", {
        answers: finalAnswers,
      });
      setFinalResult(r.data);
      if (r.data?.state) setCompletionState(r.data.state);
      if (r.data.passed) {
        toast.success(`PASSED — ${r.data.percentage}% (${r.data.score}/${r.data.total}).`);
      } else if (r.data.status === "PENDING_CERTIFICATION_REVIEW") {
        toast.error(`Pending Certification Review — ${r.data.percentage}% (${r.data.score}/${r.data.total}).`);
      } else {
        toast.error(`Not yet passed — ${r.data.percentage}% (${r.data.score}/${r.data.total}). One attempt remaining.`);
      }
      refreshCompletionState();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Final Certification Assessment submission failed.";
      toast.error(typeof msg === "string" ? msg : "Final Certification Assessment submission failed.");
    } finally {
      setFinalSubmitting(false);
    }
  };

  const retakeFinalAssessment = () => {
    if (finalAttemptsRemaining <= 0 || finalPending || finalPassed) return;
    setFinalAnswers({});
    setFinalResult(null);
    const finalSection = sessions.find((s) => s.section_kind === "final_assessment");
    if (!finalSection) return;
    const firstQIdx = (finalSection.screens || []).findIndex((s) => s.kind === "final_assessment_question");
    if (firstQIdx >= 0) {
      setActiveSessionOrder(finalSection.order);
      setPos(firstQIdx + 1);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const acceptDocument = async () => {
    const dtype = currentScreen?.document_type;
    const version = currentScreen?.document_version;
    if (!dtype || !version) return;
    if (!docChecked[dtype]) {
      toast.error("Please tick the acknowledgement checkbox before agreeing.");
      return;
    }
    try {
      setDocSubmitting(true);
      await fd2g.post("document/accept", {
        document_type: dtype,
        version,
        accepted: true,
      });
      toast.success(
        dtype === "CODE_OF_ETHICS"
          ? "Code of Ethics accepted. Facilitator Agreement is now unlocked."
          : "Facilitator Agreement accepted. Final Certification Assessment is now unlocked.",
      );
      markScreenDone(currentScreen.id);
      refreshCompletionState();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save acceptance.";
      toast.error(typeof msg === "string" ? msg : "Could not save acceptance.");
    } finally {
      setDocSubmitting(false);
    }
  };

  const goNext = () => {
    if (currentScreen) markScreenDone(currentScreen.id);
    if (pos < totalSteps - 1) {
      setPos((p) => p + 1);
    } else if (nextSection) {
      // If leaving Welcome and NOTICE is unlocked, advance to Session 1.
      // If leaving Session 1 assessment (passed), advance to Session 2 placeholder.
      setActiveSessionOrder(nextSection.order);
      setPos(1);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goPrev = () => {
    if (pos > 0) setPos((p) => p - 1);
    else if (prevSection) {
      setActiveSessionOrder(prevSection.order);
      setPos((prevSection.screens || []).length);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const jumpTo = (n) => { setPos(n); setNavOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const jumpToSection = (order, screenIndex = 1) => {
    // Block NOTICE if orientation not done.
    const targetSess = sessions.find((s) => s.order === order);
    if (targetSess && !isSessionUnlocked(targetSess)) {
      const prereq =
        order === 1 ? "Welcome & Orientation" :
        order === 2 ? "Session One \u2014 NOTICE (pass the assessment)" :
        order === 3 ? "Session Two \u2014 LISTEN (pass the assessment)" :
        order === 4 ? "Session Three \u2014 UNDERSTAND (pass the assessment)" :
        order === 5 ? "Session Four \u2014 RESPOND (pass the assessment)" :
        order === 6 ? "Session Five \u2014 HONOR (pass the assessment)" :
        order === 7 ? "Code of Ethics (accept the document)" :
        order === 8 ? "Facilitator Agreement (accept the document)" :
        order === 9 ? "Final Certification Assessment (pass at 80%)" :
        "the prior section";
      toast.info(`Complete ${prereq} to unlock this section.`);
      return;
    }
    setActiveSessionOrder(order);
    setPos(screenIndex);
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isLastScreenOfCourse = pos === totalSteps - 1 && !nextSection;

  // Next button label
  const nextLabel = (() => {
    if (currentScreen?.kind === "document_acceptance") {
      const dtype = currentScreen.document_type;
      const accepted = dtype === "CODE_OF_ETHICS" ? coeAccepted : dtype === "FACILITATOR_AGREEMENT" ? agrAccepted : false;
      if (!accepted) return currentScreen.cta_label || "I AGREE";
    }
    if (currentScreen?.kind === "orientation_ack") return currentScreen.cta_label || "Complete Orientation";
    if (currentScreen?.kind === "assessment_gate" && assessmentResult?.passed) {
      return currentScreen.on_pass?.cta_label || "Continue";
    }
    if (currentScreen?.kind === "final_assessment_gate") {
      if (finalPassed) return "Continue to Credential";
      return "Continue";
    }
    if (nextSection && pos === totalSteps - 1) {
      return `Continue to ${nextSection.key}`;
    }
    return "Continue";
  })();

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-fd2gfac-learn">
      <PreviewBanner
        course={course}
        resetTo="/academy/portal/fd2g-fac-001/learn"
        onSyncPreview={() => { setPos(0); setActiveSessionOrder(0); setKcAnswers({}); setAssessmentAnswersAll({}); setAssessmentResultsAll({}); setFinalAnswers({}); setFinalResult(null); }}
      />
      <PortalHeader learner={learner} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-10 grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Sidebar */}
        <aside className={`${navOpen ? "block" : "hidden"} lg:block`} data-testid="fd2gfac-sidebar">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sticky top-24">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">{course.code}</p>
            <h3 className="mt-1 font-heading text-base text-slate-900 leading-tight">{course.title}</h3>
            <p className="mt-1 text-[11px] text-slate-500">{course.subtitle}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-amber-700">
              {apiMode === "admin" ? "Admin Preview" : "Learner"}
            </p>

            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span>Section: {doneCount}/{screens.length}</span><span>{pct}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
              <div className="h-full bg-[#4A6FA5]" style={{ width: `${pct}%` }} />
            </div>
            {availableSessions.length > 1 && (
              <p className="mt-2 text-[10px] text-slate-400">
                Course total: {globalDoneCount}/{allWalkableScreens.length} screens ({globalPct}%)
              </p>
            )}

            <ul className="mt-5 space-y-1 text-sm" data-testid="fd2gfac-section-list">
              <li>
                <button onClick={() => jumpTo(0)} data-testid="fd2gfac-overview-link"
                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left ${currentIsOverview ? "bg-[#F0F4F8] text-[#3B597B] font-medium" : "text-slate-700 hover:bg-slate-50"}`}>
                  <Compass className="w-3.5 h-3.5" /><span>Course Overview</span>
                </button>
              </li>
              {sessions.map((sess) => {
                const Icon = sectionIcon(sess.section_kind);
                const unlocked = isSessionUnlocked(sess);
                const isCurrentSection = sess.order === activeSessionOrder && !currentIsOverview;
                const sessScreens = sess.screens || [];
                return (
                  <li key={sess.id}>
                    <details open={unlocked} className="group">
                      <summary
                        onClick={(e) => {
                          if (unlocked) { e.preventDefault(); jumpToSection(sess.order, 1); }
                        }}
                        className={`cursor-pointer list-none flex items-center gap-2 px-2.5 py-2 rounded-md ${
                          isCurrentSection ? "bg-[#EEF3FA] text-[#3B597B] font-medium" : "text-slate-600 hover:bg-slate-50"
                        } ${!unlocked ? "opacity-60" : ""}`}
                        data-testid={`fd2gfac-section-${sess.order}`}
                      >
                        {!unlocked ? <Lock className="w-3.5 h-3.5 text-slate-400" /> : <Icon className="w-3.5 h-3.5 text-[#4A6FA5]" />}
                        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">
                          {sess.section_kind === "instructional" ? `S${sess.order} · ${sess.key}` : sess.key}
                        </span>
                      </summary>
                      {unlocked && sessScreens.length > 0 && (
                        <ul className="mt-1 pl-2 border-l border-slate-100 space-y-0.5">
                          {sessScreens.map((sc, i) => {
                            const done = screenDone(sc.id);
                            const isCurr = isCurrentSection && pos === i + 1;
                            return (
                              <li key={sc.id}>
                                <button onClick={() => jumpToSection(sess.order, i + 1)} data-testid={`fd2gfac-nav-${sc.id}`}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left text-[13px] ${isCurr ? "bg-[#F0F4F8] text-[#3B597B] font-medium" : "text-slate-600 hover:bg-slate-50"}`}>
                                  {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Circle className="w-3.5 h-3.5 text-slate-300" />}
                                  <span className="truncate">{sc.eyebrow || `Screen ${i + 1}`}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {!unlocked && (
                        <p className="pl-2 mt-1 text-[11px] text-slate-400 italic">
                          {sess.order === 1 && "Complete Welcome & Orientation to unlock"}
                          {sess.order === 2 && "Pass Session One \u2014 NOTICE to unlock"}
                          {sess.order === 3 && "Pass Session Two \u2014 LISTEN to unlock"}
                          {sess.order === 4 && "Pass Session Three \u2014 UNDERSTAND to unlock"}
                          {sess.order === 5 && "Pass Session Four \u2014 RESPOND to unlock"}
                          {sess.order === 6 && "Pass Session Five \u2014 HONOR to unlock"}
                          {sess.order === 7 && "Accept the Code of Ethics to unlock"}
                          {sess.order === 8 && "Accept the Facilitator Agreement to unlock"}
                          {sess.order === 9 && "Pass the Final Certification Assessment to unlock"}
                        </p>
                      )}
                    </details>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <main className="min-w-0">
          <div className="lg:hidden mb-3 flex justify-end">
            <button onClick={() => setNavOpen((v) => !v)}
                    className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
                    data-testid="fd2gfac-nav-toggle">
              <Menu className="w-4 h-4" /> {navOpen ? "Hide sections" : "Show sections"}
            </button>
          </div>

          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
            {activeSession ? `${sectionKindLabel(activeSession)} · Step ${pos + 1} of ${totalSteps}` : ""}
          </p>

          <div className="mt-4">
            {currentIsOverview ? (
              <OverviewCard course={course} section={activeSession} noticeUnlocked={orientationDone} noticePassed={noticePassed} />
            ) : (
              <ScreenCard
                screen={currentScreen}
                section={activeSession}
                orientationChecked={orientationChecked}
                setOrientationChecked={setOrientationChecked}
                orientationDone={orientationDone}
                orientationSubmitting={orientationSubmitting}
                onSubmitOrientation={submitOrientation}
                kcAnswer={kcAnswers[currentScreen?.id]}
                onKcAnswer={(k, correct) => setKcAnswers((a) => ({ ...a, [currentScreen.id]: { key: k, correct } }))}
                assessmentAnswer={assessmentAnswers[currentScreen?.question_id]}
                onAssessmentAnswer={(qId, k) => setAssessmentAnswers((a) => ({ ...a, [qId]: k }))}
                assessmentResult={assessmentResult}
                assessmentSubmitting={assessmentSubmitting}
                onSubmitAssessment={submitAssessment}
                onRetakeAssessment={retakeAssessment}
                docChecked={docChecked}
                setDocChecked={setDocChecked}
                docSubmitting={docSubmitting}
                onAcceptDocument={acceptDocument}
                coeAccepted={coeAccepted}
                agrAccepted={agrAccepted}
                finalAnswer={finalAnswers[currentScreen?.question_id]}
                onFinalAnswer={(qId, k) => setFinalAnswers((a) => ({ ...a, [qId]: k }))}
                finalResult={finalResult}
                finalSubmitting={finalSubmitting}
                onSubmitFinal={submitFinalAssessment}
                onRetakeFinal={retakeFinalAssessment}
                finalStatus={finalStatus}
                finalPassed={finalPassed}
                finalPending={finalPending}
                finalAttemptsUsed={finalAttemptsUsed}
                finalAttemptsRemaining={finalAttemptsRemaining}
                completionState={completionState}
                credentialUnlocked={credentialUnlocked}
                credentialInfo={credentialInfo}
                credentialIssued={credentialIssued}
                credentialSubmitting={credentialSubmitting}
                onIssueCredential={issueCredential}
                onDownloadCertificate={downloadCertificate}
                onViewCredential={viewCredential}
              />
            )}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 justify-between border-t border-slate-200 pt-6">
            {(pos > 0 || prevSection) ? (
              <button onClick={goPrev}
                      data-testid="fd2gfac-prev-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:border-[#4A6FA5]">
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
            ) : (
              <span aria-hidden="true" className="hidden sm:inline-block" />
            )}

            {isLastScreenOfCourse ? (
              <Link to="/care-portal/admin/academy-portal" data-testid="fd2gfac-return-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B]">
                Return to Admin Portal <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <button
                onClick={goNext}
                disabled={!currentCanAdvance}
                data-testid="fd2gfac-next-btn"
                title={!currentCanAdvance ? "Complete this step to continue." : ""}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {nextLabel} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </main>
      </div>

      {/* View Credential modal — read-only snapshot of the authoritative
          server-side credential record. Does NOT issue or alter credential. */}
      <ViewCredentialModal
        open={viewCredentialOpen}
        loading={viewCredentialLoading}
        record={viewCredentialRecord}
        onClose={() => setViewCredentialOpen(false)}
        onDownloadCertificate={downloadCertificate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverviewCard
// ---------------------------------------------------------------------------
function OverviewCard({ course, section, noticeUnlocked, noticePassed }) {
  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="fd2gfac-overview-card">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">{course.code} · {course.badge}</p>
      <h1 className="mt-2 font-heading text-2xl md:text-3xl text-slate-900 leading-tight">{course.title}</h1>
      <p className="mt-1 text-sm text-slate-500">{course.subtitle}</p>

      {course.core_statement && (
        <div className="mt-5 rounded-lg bg-[#FBF8F1] border-l-4 border-[#C9A55C] px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A7238] font-semibold">Core Statement</p>
          <p className="mt-1 text-slate-800 italic">{course.core_statement}</p>
        </div>
      )}
      {course.core_principle && (
        <div className="mt-3 rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#3B597B] font-semibold">Core Facilitator Principle</p>
          <p className="mt-1 text-slate-800">"{course.core_principle}"</p>
        </div>
      )}

      {section && (
        <div className="mt-6 pt-5 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{sectionKindLabel(section)}</p>
          <h2 className="mt-1 font-heading text-lg text-slate-900">
            {section.key === "WELCOME" ? section.title : `${section.key} — ${section.title.replace(/^[A-Z\s]+—\s*/, "")}`}
          </h2>
          {section.subtitle && <p className="mt-1 text-sm text-slate-600">{section.subtitle}</p>}
          {section.order === 0 && !noticeUnlocked && (
            <p className="mt-3 text-[12px] text-amber-700" data-testid="fd2gfac-orientation-required-hint">
              Complete the four orientation acknowledgements at the end of Welcome & Orientation to unlock Session One — NOTICE.
            </p>
          )}
          {section.order === 1 && noticePassed && (
            <p className="mt-3 text-[12px] text-emerald-700" data-testid="fd2gfac-notice-completed-hint">
              Session One — NOTICE assessment passed. Sessions 2-5 remain in development.
            </p>
          )}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// ScreenCard — renders any screen kind used in Phase 2
// ---------------------------------------------------------------------------
function ScreenCard(props) {
  const { screen, section } = props;
  if (!screen) return null;

  // Issue 2/5 — suppress duplicate heading when the same text is already
  // rendered inside the body:
  //  • assessment_question and final_assessment_question always duplicate
  //    the prompt (built with section=q.prompt); render prompt only.
  //  • content screens whose only self_check block title equals the screen
  //    section (e.g., "LISTEN Self-Check" heading + Self-Check block title).
  const blocks = screen.blocks || [];
  const selfCheckSameAsSection = blocks.some(
    (b) => b?.type === "self_check" && b?.title && b.title === screen.section,
  );
  const isAssessmentQ =
    screen.kind === "assessment_question" || screen.kind === "final_assessment_question";
  const sectionSuppressed = isAssessmentQ || selfCheckSameAsSection;

  // Issue 4 — suppress the session governing_principle footer when a
  // principle block on this screen already renders the same statement.
  const gp = section?.governing_principle;
  const gpAlreadyOnScreen = !!gp && blocks.some(
    (b) => b?.type === "principle" && (b?.text || "").trim() === gp.trim(),
  );

  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid={`fd2gfac-screen-${screen.id}`}>
      {screen.eyebrow && (
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">{screen.eyebrow}</p>
      )}
      {screen.section && !sectionSuppressed && (
        <h1 className="mt-2 font-heading text-xl md:text-2xl text-slate-900 leading-tight">{decodeDisplayEntities(screen.section)}</h1>
      )}

      {/* Body by kind */}
      {screen.kind === "content" && (
        <div className="mt-5 space-y-4">
          {(screen.blocks || []).map((b, i) => <Block key={i} block={b} />)}
        </div>
      )}

      {screen.kind === "reflection" && <ReflectionScreen screen={screen} />}

      {screen.kind === "orientation_ack" && (
        <OrientationAck
          screen={screen}
          checked={props.orientationChecked}
          setChecked={props.setOrientationChecked}
          submitting={props.orientationSubmitting}
          done={props.orientationDone}
          onSubmit={props.onSubmitOrientation}
        />
      )}

      {screen.kind === "knowledge_check" && (
        <KnowledgeCheck screen={screen} answer={props.kcAnswer} onAnswer={props.onKcAnswer} />
      )}

      {screen.kind === "assessment_intro" && (
        <div className="mt-5 space-y-4">
          {(screen.blocks || []).map((b, i) => <Block key={i} block={b} />)}
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
            Answers are saved only when you submit at the end of the assessment.
          </div>
        </div>
      )}

      {screen.kind === "assessment_question" && (
        <AssessmentQuestion
          screen={screen}
          answer={props.assessmentAnswer}
          onAnswer={(k) => props.onAssessmentAnswer(screen.question_id, k)}
        />
      )}

      {screen.kind === "assessment_gate" && (
        <AssessmentGate
          screen={screen}
          result={props.assessmentResult}
          submitting={props.assessmentSubmitting}
          onSubmit={props.onSubmitAssessment}
          onRetake={props.onRetakeAssessment}
        />
      )}

      {screen.kind === "final_assessment_intro" && (
        <FinalAssessmentIntro
          screen={screen}
          finalStatus={props.finalStatus}
          finalAttemptsUsed={props.finalAttemptsUsed}
          finalAttemptsRemaining={props.finalAttemptsRemaining}
          finalPassed={props.finalPassed}
          finalPending={props.finalPending}
        />
      )}

      {screen.kind === "final_assessment_question" && (
        <FinalAssessmentQuestion
          screen={screen}
          answer={props.finalAnswer}
          onAnswer={(k) => props.onFinalAnswer(screen.question_id, k)}
          disabled={props.finalPassed || props.finalPending}
        />
      )}

      {screen.kind === "final_assessment_gate" && (
        <FinalAssessmentGate
          screen={screen}
          result={props.finalResult}
          submitting={props.finalSubmitting}
          onSubmit={props.onSubmitFinal}
          onRetake={props.onRetakeFinal}
          finalStatus={props.finalStatus}
          finalPassed={props.finalPassed}
          finalPending={props.finalPending}
          finalAttemptsUsed={props.finalAttemptsUsed}
          finalAttemptsRemaining={props.finalAttemptsRemaining}
          completionState={props.completionState}
        />
      )}

      {screen.kind === "document_acceptance" && (
        <DocumentAcceptance
          screen={screen}
          checked={props.docChecked}
          setChecked={props.setDocChecked}
          submitting={props.docSubmitting}
          onAccept={props.onAcceptDocument}
          coeAccepted={props.coeAccepted}
          agrAccepted={props.agrAccepted}
        />
      )}

      {/* Placeholder kinds — unchanged from Phase 1 */}
      {screen.kind === "placeholder" && (
        <PlaceholderBody screen={screen} />
      )}
      {screen.kind === "placeholder_assessment" && (
        <PlaceholderAssessmentBody screen={screen} />
      )}
      {screen.kind === "placeholder_ack" && (
        <PlaceholderAckBody screen={screen} />
      )}
      {screen.kind === "placeholder_credential" && (
        <PlaceholderCredentialBody
          screen={screen}
          credentialUnlocked={props.credentialUnlocked}
          credentialInfo={props.credentialInfo}
          credentialIssued={props.credentialIssued}
          credentialSubmitting={props.credentialSubmitting}
          onIssueCredential={props.onIssueCredential}
          onDownloadCertificate={props.onDownloadCertificate}
          onViewCredential={props.onViewCredential}
        />
      )}

      {section?.governing_principle && screen.kind !== "assessment_gate" && !gpAlreadyOnScreen && (
        <p className="mt-6 text-[11px] italic text-slate-400">{decodeDisplayEntities(section.governing_principle)}</p>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Block renderer (content blocks)
// ---------------------------------------------------------------------------
function Block({ block }) {
  const t = block.type;
  if (t === "paragraph") return <p className="text-slate-700 leading-relaxed">{decodeDisplayEntities(block.text)}</p>;
  if (t === "emphasis") return <p className="text-lg font-heading italic text-slate-900">{decodeDisplayEntities(block.text)}</p>;
  if (t === "subheading") return <h3 className="mt-3 text-[13px] uppercase tracking-[0.16em] font-semibold text-slate-700">{decodeDisplayEntities(block.text)}</h3>;
  if (t === "list") return (
    <ul className="space-y-1 pl-1">
      {(block.items || []).map((it, i) => (
        <li key={i} className="flex items-start gap-2 text-slate-700">
          <span className="mt-2 w-1 h-1 rounded-full bg-slate-400 shrink-0" />
          <span className="leading-relaxed">{decodeDisplayEntities(it)}</span>
        </li>
      ))}
    </ul>
  );
  if (t === "numbered_list") return (
    <ol className="space-y-1.5 pl-1 list-none counter-reset">
      {(block.items || []).map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-slate-700">
          <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#F0F4F8] text-[#3B597B] text-xs font-semibold flex items-center justify-center">{i + 1}</span>
          <span className="leading-relaxed">{decodeDisplayEntities(it)}</span>
        </li>
      ))}
    </ol>
  );
  if (t === "principle") {
    // Author data in fd2g_fac_001.py tags principle blocks with internal
    // placement directives ("Display" / "Display Prominently"). These are
    // production/typography hints for designers, not learner-facing labels.
    // We map them to learner-safe copy here without touching the frozen
    // curriculum data. Any non-directive label (e.g. "Core Principle") is
    // rendered as authored.
    const rawLabel = (block.label || "").trim();
    const isPlacementDirective = rawLabel === "Display" || rawLabel === "Display Prominently";
    const displayLabel = isPlacementDirective
      ? (rawLabel === "Display Prominently" ? "Key Principle" : "Principle")
      : (rawLabel || "Principle");
    return (
      <div className="rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#3B597B]">{decodeDisplayEntities(displayLabel)}</p>
        <p className="mt-1 text-slate-900 font-heading">{decodeDisplayEntities(block.text)}</p>
      </div>
    );
  }
  if (t === "quote") return (
    <blockquote className="rounded-lg bg-[#FBF8F1] border-l-4 border-[#C9A55C] px-4 py-3">
      <p className="text-slate-800 italic leading-relaxed">"{decodeDisplayEntities(block.text)}"</p>
    </blockquote>
  );
  if (t === "callout") {
    const tone = block.tone || "blue";
    const cls = tone === "amber"
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : "bg-[#EEF3FA] border-[#B7C7DB] text-[#2E4C6E]";
    return (
      <div className={`rounded-lg border px-4 py-3 ${cls}`}>
        {block.label && <p className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-80">{block.label}</p>}
        <p className={`${block.label ? "mt-1" : ""} leading-relaxed`}>{block.text}</p>
      </div>
    );
  }
  if (t === "stack") return (
    <div className="space-y-1">
      {(block.items || []).map((it, i) => (
        <p key={i} className="text-slate-700 leading-relaxed">{it}</p>
      ))}
    </div>
  );
  if (t === "voice_card") return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">{block.key}</p>
      <p className="mt-1 font-heading text-base text-slate-900">{block.title}</p>
      <p className="mt-2 text-sm text-slate-600 italic">Central question: {block.central_question}</p>
    </div>
  );
  if (t === "voices_summary") return (
    <div className="rounded-xl bg-[#F0F4F8] border border-[#B7C7DB] px-4 py-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#3B597B]">The Five Voices</p>
      <ul className="mt-2 space-y-0.5">
        {(block.lines || []).map((l, i) => (
          <li key={i} className="font-heading text-slate-900">{l}</li>
        ))}
      </ul>
    </div>
  );
  if (t === "self_check") return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">{block.title}</p>
      <ul className="mt-2 space-y-2">
        {(block.items || []).map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-0.5 shrink-0 text-[11px] font-semibold text-[#4A6FA5]">{it.key}</span>
            <span className="text-sm text-slate-700 leading-relaxed">{it.prompt}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  if (t === "pathway") return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">Certification Pathway</p>
      <ol className="mt-2 space-y-1.5">
        {(block.steps || []).map((s, i, arr) => (
          <li key={i} className="flex items-center gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-[#F0F4F8] text-[#3B597B] text-xs font-semibold flex items-center justify-center">{i + 1}</span>
            <span className="text-sm text-slate-800">{s}</span>
            {i < arr.length - 1 && <span className="ml-auto text-slate-300">↓</span>}
          </li>
        ))}
      </ol>
    </div>
  );
  if (t === "book_foundation") return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">Book Foundation</p>
      {block.primary && <p className="mt-1 text-sm text-slate-800"><span className="font-semibold">Primary:</span> {block.primary}</p>}
      {block.supporting && <p className="text-sm text-slate-600"><span className="font-semibold">Supporting:</span> {block.supporting}</p>}
    </div>
  );
  if (t === "labeled_line") return (
    <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-600">{block.label}</p>
      <p className="mt-0.5 text-slate-800 leading-relaxed">{block.text}</p>
    </div>
  );
  if (t === "numbered_lens") return (
    <ol className="space-y-2">
      {(block.items || []).map((it, i) => (
        <li key={i} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-[#F0F4F8] text-[#3B597B] text-xs font-semibold flex items-center justify-center">{i + 1}</span>
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">{it.heading}</p>
          </div>
          <p className="mt-1 text-sm text-slate-700 leading-relaxed">{it.text}</p>
        </li>
      ))}
    </ol>
  );
  if (t === "flow") {
    const tone = block.tone === "avoid" ? "text-rose-700 border-rose-200 bg-rose-50" : "text-[#3B597B] border-[#B7C7DB] bg-[#F0F4F8]";
    return (
      <div className={`inline-flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-4 py-3 ${tone}`}>
        {(block.steps || []).map((s, i, arr) => (
          <span key={i} className="flex items-center gap-3 text-[11px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap">
            <span>{s}</span>
            {i < arr.length - 1 && <span aria-hidden="true" className="opacity-60">→</span>}
          </span>
        ))}
      </div>
    );
  }
  if (t === "book_parts") return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">Book Foundation \u2014 The Whole Journey</p>
      <ul className="mt-2 space-y-1.5">
        {(block.parts || []).map((p, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="shrink-0 text-[10px] uppercase tracking-[0.14em] font-semibold text-[#3B597B] w-14">{p.key}</span>
            <span className="text-sm text-slate-800 leading-relaxed">{p.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
  if (t === "voice_prompts") return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <ul className="space-y-2">
        {(block.items || []).map((it, i) => (
          <li key={i} className="rounded-lg bg-[#F8FAFC] border border-slate-100 px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">{it.key}</p>
            <p className="mt-0.5 text-sm text-slate-800 leading-relaxed">{it.prompt}</p>
          </li>
        ))}
      </ul>
    </div>
  );
  if (t === "voice_map") return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="fd2gfac-voice-map">
      <div className="px-4 py-3 border-b border-slate-100 bg-[#F8FAFC]">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">FD2G Voice Map</p>
        <p className="text-[11px] text-slate-500 mt-0.5">A working tool the facilitator uses to separate evidence from interpretation.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-700">
              {(block.columns || []).map((c, i) => (
                <th key={i} className="text-left px-4 py-2 font-semibold border-b border-slate-200 whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(block.voices || []).map((v, i) => (
              <tr key={i} className="border-b last:border-b-0 border-slate-100">
                <td className="px-4 py-3 align-top font-medium text-slate-900 whitespace-nowrap">{v}</td>
                <td className="px-4 py-3 align-top text-slate-500 italic">Evidence only \u2014 no assumptions.</td>
                <td className="px-4 py-3 align-top text-slate-500 italic">Curiosity, open questions.</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  return null;
}

// ---------------------------------------------------------------------------
// Reflection screen
// ---------------------------------------------------------------------------
function ReflectionScreen({ screen }) {
  // Issue 8 — suppress the trailing privacy note when the intro already
  // conveys the same privacy promise (contains "not stored" or "not scored").
  const introText = decodeDisplayEntities(screen.intro || "");
  const noteText = decodeDisplayEntities(screen.note || "");
  const introSaysPrivate = /not stored|not scored/i.test(introText);
  const noteSaysPrivate = /not stored|not scored/i.test(noteText);
  const showNote = !!screen.note && !(introSaysPrivate && noteSaysPrivate);
  return (
    <div className="mt-5 space-y-4">
      {screen.intro && <p className="text-slate-700 leading-relaxed">{introText}</p>}
      <ol className="space-y-2 list-none">
        {(screen.prompts || []).map((p, i) => (
          <li key={i} className="rounded-lg border border-slate-200 bg-[#F8FAFC] px-4 py-3" data-testid={`fd2gfac-reflection-${i + 1}`}>
            <p className="text-sm text-slate-800 leading-relaxed font-medium">{decodeDisplayEntities(p)}</p>
            <textarea rows={2} placeholder="For your own reflection. Not stored."
                      className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] resize-none" />
          </li>
        ))}
      </ol>
      {showNote && <p className="text-[11px] text-slate-500 italic">{noteText}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Orientation acknowledgement (4 required checkboxes)
// ---------------------------------------------------------------------------
function OrientationAck({ screen, checked, setChecked, submitting, done, onSubmit }) {
  const items = screen.acknowledgements || [];
  const allChecked = items.every((it) => checked[it.id]);
  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="mt-5 space-y-4">
      {screen.intro && <p className="text-slate-700 leading-relaxed">{screen.intro}</p>}
      <ul className="space-y-2">
        {items.map((it) => {
          const isChecked = !!checked[it.id];
          return (
            <li key={it.id}>
              <label
                className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer ${
                  isChecked ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:border-[#4A6FA5]"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(it.id)}
                  data-testid={`fd2gfac-orientation-ack-${it.id}`}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]"
                />
                <span className="text-sm text-slate-800 leading-relaxed">{it.text}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          onClick={onSubmit}
          disabled={!allChecked || submitting || done}
          data-testid="fd2gfac-orientation-submit"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Saving…" : done ? "Orientation Complete" : (screen.cta_label || "Complete Orientation")}
        </button>
        {done && (
          <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700" data-testid="fd2gfac-orientation-complete-badge">
            <CheckCircle2 className="w-4 h-4" /> Session One — NOTICE is unlocked
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document acceptance (Code of Ethics / Facilitator Agreement) — versioned
// ---------------------------------------------------------------------------
function DocumentAcceptance({ screen, checked, setChecked, submitting, onAccept, coeAccepted, agrAccepted }) {
  const dtype = screen.document_type;
  const version = screen.document_version;
  const isCoe = dtype === "CODE_OF_ETHICS";
  const alreadyAccepted = isCoe ? coeAccepted : agrAccepted;
  const isChecked = !!checked[dtype];
  const toggle = () => setChecked((prev) => ({ ...prev, [dtype]: !prev[dtype] }));

  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-lg bg-[#FBF8F1] border-l-4 border-[#C9A55C] px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A7238] font-semibold">Commitment</p>
        <p className="mt-1 text-slate-800 leading-relaxed">{screen.commitment}</p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-500">
        Document version: <span className="font-semibold text-slate-700">{version}</span>. Acceptance is recorded server-side with a timestamp for audit.
      </div>

      <label
        className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer ${
          isChecked ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:border-[#4A6FA5]"
        } ${alreadyAccepted ? "opacity-70 pointer-events-none" : ""}`}
      >
        <input
          type="checkbox"
          checked={isChecked || alreadyAccepted}
          onChange={toggle}
          disabled={alreadyAccepted}
          data-testid={`fd2gfac-doc-checkbox-${dtype}`}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]"
        />
        <span className="text-sm text-slate-800 leading-relaxed">{screen.checkbox_label}</span>
      </label>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-2">
        <button
          onClick={onAccept}
          disabled={alreadyAccepted || !isChecked || submitting}
          data-testid={`fd2gfac-doc-accept-${dtype}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? "Saving\u2026" : alreadyAccepted ? "Accepted" : (screen.cta_label || "I AGREE")}
        </button>
        {alreadyAccepted && (
          <span
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 pl-3 border-l border-emerald-200"
            data-testid={`fd2gfac-doc-accepted-badge-${dtype}`}
          >
            <CheckCircle2 className="w-4 h-4" />
            {isCoe ? "Facilitator Agreement is now unlocked" : "Final Certification Assessment is now unlocked"}
          </span>
        )}
      </div>

      {screen.gate_message && !alreadyAccepted && (
        <p className="text-[11px] text-slate-500 italic">{screen.gate_message}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Knowledge check (Practice Contrast)
// ---------------------------------------------------------------------------
function KnowledgeCheck({ screen, answer, onAnswer }) {
  const submitted = !!answer;
  const correctKey = screen.correct;
  return (
    <div className="mt-5 space-y-3">
      {screen.prompt && <p className="text-slate-700 leading-relaxed">{screen.prompt}</p>}
      <ul className="space-y-2" role="radiogroup">
        {(screen.options || []).map((op) => {
          const isSel = answer?.key === op.key;
          const isCorr = submitted && op.key === correctKey;
          const cls = !submitted
            ? "border-slate-200 bg-white hover:border-[#4A6FA5]"
            : isCorr ? "border-emerald-300 bg-emerald-50"
            : isSel ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white opacity-70";
          return (
            <li key={op.key}>
              <button
                type="button"
                role="radio"
                aria-checked={isSel}
                onClick={() => !submitted && onAnswer(op.key, op.key === correctKey)}
                disabled={submitted}
                data-testid={`fd2gfac-kc-${screen.id}-opt-${op.key}`}
                className={`w-full text-left rounded-lg border px-4 py-3 text-sm text-slate-800 ${cls} focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]`}
              >
                <span className="font-semibold mr-2">{op.key.toUpperCase()}.</span>{op.label}
                {submitted && isCorr && <span className="ml-2 text-[11px] font-semibold text-emerald-700">Correct answer</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {submitted && screen.feedback && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {screen.feedback}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assessment question (no correct-key exposed pre-submit)
// ---------------------------------------------------------------------------
function AssessmentQuestion({ screen, answer, onAnswer }) {
  return (
    <div className="mt-5 space-y-3">
      <p className="text-slate-700 leading-relaxed">{decodeDisplayEntities(screen.prompt)}</p>
      <ul className="space-y-2" role="radiogroup">
        {(screen.options || []).map((op) => {
          const isSel = answer === op.key;
          return (
            <li key={op.key}>
              <button
                type="button"
                role="radio"
                aria-checked={isSel}
                onClick={() => onAnswer(op.key)}
                data-testid={`fd2gfac-aq-${screen.question_id}-opt-${op.key}`}
                className={`w-full text-left rounded-lg border px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] ${
                  isSel ? "border-[#4A6FA5] bg-[#F0F4F8]" : "border-slate-200 bg-white hover:border-[#4A6FA5]"
                }`}
              >
                <span className="font-semibold mr-2">{op.key.toUpperCase()}.</span>{decodeDisplayEntities(op.label)}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assessment gate — Submit + result view + retake
// ---------------------------------------------------------------------------
function AssessmentGate({ screen, result, submitting, onSubmit, onRetake }) {
  if (!result) {
    return (
      <div className="mt-5 space-y-4">
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
          {screen.total_questions} questions · {screen.passing_percent}% required to pass · retakes permitted.
        </div>
        <button
          onClick={onSubmit}
          disabled={submitting}
          data-testid="fd2gfac-assessment-submit"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-50"
        >
          {submitting ? "Scoring…" : "Submit Assessment"}
        </button>
      </div>
    );
  }
  if (result.passed) {
    const tc = screen.on_pass?.training_complete_display;
    // Distinguish current attempt from preserved prior pass. Server returns
    // `this_attempt_passed` for the submission just made, while `passed`
    // reflects the (preserved) overall pass state (prev_passed OR passed).
    const thisAttemptPassed = result.this_attempt_passed !== false;
    return (
      <div className="mt-5 space-y-4">
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-4" data-testid="fd2gfac-assessment-passed">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-700">Result</p>
          <h2 className="mt-1 font-heading text-xl text-emerald-900">{screen.on_pass?.heading || "PASSED"}</h2>
          {screen.on_pass?.text && thisAttemptPassed && <p className="mt-1 text-sm text-emerald-800">{screen.on_pass.text}</p>}
          {thisAttemptPassed ? (
            <p className="mt-3 text-sm text-emerald-900" data-testid="fd2gfac-assessment-this-attempt-passed">
              You passed this assessment: {result.percent}% ({result.score}/{result.total}). Your passing result has been recorded.
            </p>
          ) : (
            <div className="mt-3 space-y-1" data-testid="fd2gfac-assessment-preserved-pass">
              <p className="text-sm text-emerald-900">
                This attempt did not reach 80%: {result.percent}% ({result.score}/{result.total}).
              </p>
              <p className="text-sm text-emerald-900">
                Your previous passing result remains on record — this session remains complete, and your progression is unaffected.
              </p>
            </div>
          )}
        </div>
        {tc && thisAttemptPassed && (
          <div className="rounded-xl border-2 border-[#C9A55C] bg-[#FBF8F1] px-5 py-5" data-testid="fd2gfac-training-complete">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7238]">Milestone</p>
            <h2 className="mt-1 font-heading text-2xl text-slate-900">{tc.heading}</h2>
            <ul className="mt-3 space-y-0.5">
              {(tc.lines || []).map((l, i) => <li key={i} className="font-heading text-slate-900">{l}</li>)}
            </ul>
            {tc.closing && <p className="mt-4 text-sm text-slate-700 leading-relaxed">{tc.closing}</p>}
          </div>
        )}
      </div>
    );
  }
  // Not yet passed — never show complete answer key
  const missed = (result.review || []).filter((r) => !r.is_correct).length;
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-4" data-testid="fd2gfac-assessment-not-passed">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-rose-700">Result</p>
        <h2 className="mt-1 font-heading text-xl text-rose-900">{screen.on_fail?.heading || "NOT YET PASSED"}</h2>
        <p className="mt-1 text-sm text-rose-800">Score {result.percent}% ({result.score}/{result.total}). {missed} incorrect. 80% required to pass. The complete answer key is not shown.</p>
        <p className="mt-2 text-[12px] text-rose-800">{screen.on_fail?.text || "Review the NOTICE material and retake the assessment."}</p>
      </div>
      <button
        onClick={onRetake}
        data-testid="fd2gfac-assessment-retake"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:border-[#4A6FA5]"
      >
        <RefreshCw className="w-4 h-4" /> Retake Assessment
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Final Certification Assessment — intro, question, and gate/result
// ---------------------------------------------------------------------------
function FinalAssessmentIntro({ screen, finalStatus, finalAttemptsUsed, finalAttemptsRemaining, finalPassed, finalPending }) {
  return (
    <div className="mt-5 space-y-4" data-testid="fd2gfac-final-intro">
      {(screen.blocks || []).map((b, i) => <Block key={i} block={b} />)}
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
        <p><span className="font-semibold">Attempts used:</span> {finalAttemptsUsed} of {screen.max_attempts || 2}. <span className="font-semibold">Remaining:</span> {finalAttemptsRemaining}.</p>
        <p className="mt-1"><span className="font-semibold">Status:</span> {finalStatus.replaceAll("_", " ")}</p>
        {finalPassed && <p className="mt-2 text-emerald-700 font-semibold">You have passed the Final Certification Assessment.</p>}
        {finalPending && <p className="mt-2 text-rose-700 font-semibold">Assessment record is in Pending Certification Review. No further learner attempt is available.</p>}
      </div>
    </div>
  );
}

function FinalAssessmentQuestion({ screen, answer, onAnswer, disabled }) {
  return (
    <div className="mt-5 space-y-3">
      <p className="text-slate-700 leading-relaxed">{screen.prompt}</p>
      <ul className="space-y-2" role="radiogroup">
        {(screen.options || []).map((op) => {
          const isSel = answer === op.key;
          return (
            <li key={op.key}>
              <button
                type="button"
                role="radio"
                aria-checked={isSel}
                disabled={disabled}
                onClick={() => !disabled && onAnswer(op.key)}
                data-testid={`fd2gfac-final-q-${screen.question_id}-opt-${op.key}`}
                className={`w-full text-left rounded-lg border px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] ${
                  isSel ? "border-[#4A6FA5] bg-[#F0F4F8]" : "border-slate-200 bg-white hover:border-[#4A6FA5]"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <span className="font-semibold mr-2">{op.key.toUpperCase()}.</span>{op.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function FinalAssessmentGate({ screen, result, submitting, onSubmit, onRetake, finalStatus, finalPassed, finalPending, finalAttemptsUsed, finalAttemptsRemaining, completionState }) {
  // Prefer live server state over the transient in-memory result — persistence across reloads.
  const state = completionState?.final_assessment || {};
  const displayScore = result?.score ?? state.best_score ?? 0;
  const displayPct = result?.percentage ?? state.best_percentage ?? 0;
  const displayTotal = result?.total ?? screen.total_questions ?? 20;
  const displayLabels = (result?.review_labels && result.review_labels.length)
    ? result.review_labels
    : (state.last_review_labels || []);

  // PASSED — either fresh submission or persisted PASSED status.
  if (finalPassed || (result && result.passed)) {
    return (
      <div className="mt-5 space-y-5" data-testid="fd2gfac-final-passed">
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-4">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-700">Result</p>
          <h2 className="mt-1 font-heading text-xl text-emerald-900">FINAL CERTIFICATION ASSESSMENT — PASSED</h2>
          <p className="mt-2 text-sm text-emerald-900">
            Your score: {displayScore}/{displayTotal} · {displayPct}%
          </p>
          <p className="mt-2 text-sm text-emerald-900">You have successfully completed the Final Certification Assessment.</p>
        </div>
        <div className="rounded-xl border-2 border-[#C9A55C] bg-[#FBF8F1] px-5 py-5" data-testid="fd2gfac-cert-requirements-complete">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7238]">Milestone</p>
          <h2 className="mt-1 font-heading text-2xl text-slate-900">CERTIFICATION REQUIREMENTS COMPLETE</h2>
          <p className="mt-2 text-sm text-slate-700">You have completed:</p>
          <ul className="mt-3 space-y-1 text-slate-800">
            <li>✓ Welcome & Orientation</li>
            <li>✓ NOTICE</li>
            <li>✓ LISTEN</li>
            <li>✓ UNDERSTAND</li>
            <li>✓ RESPOND</li>
            <li>✓ HONOR</li>
            <li>✓ FD2G Certified Facilitator Code of Ethics</li>
            <li>✓ FD2G Certified Facilitator Agreement</li>
            <li>✓ Final Certification Assessment</li>
          </ul>
          <p className="mt-4 text-sm text-slate-700">Your certification requirements are complete.</p>
          <p className="mt-2 text-[12px] italic text-slate-500">
            Credential issuance is not active in this preview environment.
          </p>
        </div>
      </div>
    );
  }

  // PENDING CERTIFICATION REVIEW — terminal, no retake.
  if (finalPending || (result && result.status === "PENDING_CERTIFICATION_REVIEW")) {
    return (
      <div className="mt-5 space-y-4" data-testid="fd2gfac-final-pending-review">
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-4">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-rose-700">Result</p>
          <h2 className="mt-1 font-heading text-xl text-rose-900">FINAL CERTIFICATION ASSESSMENT — PENDING CERTIFICATION REVIEW</h2>
          <p className="mt-2 text-sm text-rose-900">Your score: {displayScore}/{displayTotal} · {displayPct}%</p>
          <p className="mt-3 text-sm text-rose-900">
            Your second assessment attempt did not reach the required 80% passing score.
          </p>
          <p className="mt-2 text-sm text-rose-900">Your certification has not been issued.</p>
          <p className="mt-2 text-sm text-rose-900">Your assessment record has been placed in Pending Certification Review status.</p>
          <p className="mt-3 text-[12px] italic text-rose-800">
            No additional learner attempt is available.
          </p>
        </div>
      </div>
    );
  }

  // ATTEMPT_1_NOT_PASSED — show generalized competency review + Begin Attempt Two.
  if (result && !result.passed) {
    return (
      <div className="mt-5 space-y-4" data-testid="fd2gfac-final-attempt1-fail">
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-4">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-rose-700">Result</p>
          <h2 className="mt-1 font-heading text-xl text-rose-900">FINAL CERTIFICATION ASSESSMENT — NOT YET PASSED</h2>
          <p className="mt-2 text-sm text-rose-900">Your score: {displayScore}/{displayTotal} · {displayPct}%</p>
          <p className="mt-2 text-sm text-rose-900">A score of 80% is required for certification.</p>
          <p className="mt-3 text-sm text-rose-900">
            Before attempting the assessment again, review the Five Voices and the competency areas identified below.
          </p>
          {displayLabels.length > 0 && (
            <div className="mt-3" data-testid="fd2gfac-final-competency-review">
              <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-rose-800">Review</p>
              <ul className="mt-2 space-y-1 text-sm text-rose-900 list-disc pl-5">
                {displayLabels.map((label, i) => <li key={i}>{label}</li>)}
              </ul>
            </div>
          )}
          <p className="mt-4 text-[12px] italic text-rose-800">
            The complete answer key is not shown. You have {finalAttemptsRemaining} assessment attempt{finalAttemptsRemaining === 1 ? "" : "s"} remaining.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            data-testid="fd2gfac-final-review-course-cta"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:border-[#4A6FA5]"
          >
            REVIEW COURSE
          </a>
          <button
            onClick={onRetake}
            disabled={finalAttemptsRemaining <= 0}
            data-testid="fd2gfac-final-attempt2-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" /> BEGIN ATTEMPT TWO
          </button>
        </div>
      </div>
    );
  }

  // Default — pre-submit gate.
  const attemptLabel = finalAttemptsUsed === 0 ? "ATTEMPT ONE" : "ATTEMPT TWO";
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
        {screen.total_questions} questions · {screen.passing_percent}% required to pass ({screen.passing_min}/{screen.total_questions}) · maximum {screen.max_attempts} attempts.
      </div>
      <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{attemptLabel}</p>
      <button
        onClick={onSubmit}
        disabled={submitting}
        data-testid="fd2gfac-final-submit"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-50"
      >
        {submitting ? "Scoring…" : "Submit Final Certification Assessment"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Placeholder screen bodies (Phase 1 shell surface, preserved unchanged)
// ---------------------------------------------------------------------------
function PlaceholderBody({ screen }) {
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-semibold">Phase 3+ Shell</p>
        <p className="mt-1 text-sm text-amber-900">{screen.shell_note}</p>
      </div>
      {Array.isArray(screen.scope_notes) && screen.scope_notes.length > 0 && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
          <p className="font-semibold text-slate-900">Screen scope (author to supply)</p>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            {screen.scope_notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
function PlaceholderAssessmentBody({ screen }) {
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-semibold">Phase 3+ Shell</p>
        <p className="mt-1 text-sm text-amber-900">{screen.shell_note}</p>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">Assessment structure</p>
        <ul className="mt-2 space-y-1 list-disc pl-5">
          <li>{screen.total_questions} multiple-choice questions.</li>
          <li>{screen.passing_percent}% required to pass.</li>
          <li>Retakes permitted.</li>
          <li>A section is not considered successfully completed for certification progression until its required assessment has been passed.</li>
        </ul>
      </div>
    </div>
  );
}
function PlaceholderAckBody({ screen }) {
  return (
    <div className="mt-5 space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-semibold">Phase 3+ Shell</p>
        <p className="mt-1 text-sm text-amber-900">{screen.shell_note}</p>
      </div>
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
        <p className="font-semibold text-slate-900">Acknowledgement structure</p>
        <ul className="mt-2 space-y-1 list-disc pl-5">
          <li>Document: {screen.doc_label}.</li>
          <li>Learner reads the document text (author-supplied in later phase).</li>
          <li>Learner formally accepts. Acceptance is a required certification step.</li>
          <li>Acceptance timestamp will be recorded on the learner's certification record.</li>
        </ul>
      </div>
    </div>
  );
}
function PlaceholderCredentialBody({ screen, credentialUnlocked, credentialInfo, credentialIssued, credentialSubmitting, onIssueCredential, onDownloadCertificate, onViewCredential }) {
  if (!credentialUnlocked) {
    return (
      <div className="mt-5 space-y-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-semibold">Locked</p>
          <p className="mt-1 text-sm text-amber-900">{screen.shell_note}</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
          <p className="font-semibold text-slate-900">Credential issuance structure</p>
          <ul className="mt-2 space-y-1 list-disc pl-5">
            <li>Credential name: <strong>{screen.credential_name}</strong>.</li>
            <li>Credential code: <strong>{screen.credential_code}</strong>.</li>
            <li>Individual Facilitator Certification — not a course-completion certificate.</li>
            <li>Issued only after every certification requirement is satisfied.</li>
          </ul>
        </div>
      </div>
    );
  }

  // Certification requirements complete — either offer issuance, or render MY CREDENTIALS.
  if (!credentialIssued) {
    return (
      <div className="mt-5 space-y-4" data-testid="fd2gfac-credential-issue-cta">
        <div className="rounded-xl border-2 border-[#C9A55C] bg-[#FBF8F1] px-5 py-5">
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7238]">Ready for Issuance</p>
          <h2 className="mt-1 font-heading text-2xl text-slate-900">Certification requirements complete</h2>
          <p className="mt-3 text-sm text-slate-700">
            Every requirement has been satisfied. You may now issue your{" "}
            <strong>Certified From Diagnosis to Goodbye Facilitator</strong> credential.
          </p>
          <p className="mt-2 text-[12px] italic text-slate-500">
            Your credential is generated from the authoritative server-side record.
          </p>
          <button
            type="button"
            onClick={onIssueCredential}
            disabled={credentialSubmitting}
            data-testid="fd2gfac-credential-issue-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B] disabled:opacity-50"
          >
            {credentialSubmitting ? "Issuing…" : "ISSUE MY CREDENTIAL"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <MyCredentialsView
      credentialInfo={credentialInfo}
      onDownloadCertificate={onDownloadCertificate}
      onViewCredential={onViewCredential}
    />
  );
}

function MyCredentialsView({ credentialInfo, onDownloadCertificate, onViewCredential }) {
  const fmt = (iso) => {
    if (!iso) return "\u2014";
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch (_) {
      return iso;
    }
  };
  const status = credentialInfo?.status || "ACTIVE";
  const statusStyles = status === "ACTIVE"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "EXPIRING SOON"
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-rose-50 text-rose-800 border-rose-200";

  return (
    <div className="mt-5 space-y-6" data-testid="fd2gfac-my-credentials">
      {/* Hero credential card */}
      <div className="rounded-xl border-2 border-[#C9A55C] bg-[#FBF8F1] px-5 py-6" data-testid="fd2gfac-credential-hero">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7238]">My Credentials</p>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl text-slate-900">
              {credentialInfo.credential_name || "Certified From Diagnosis to Goodbye Facilitator"}
            </h2>
            {credentialInfo.facilitator_name && (
              <p className="mt-1 text-sm text-slate-600">Issued to <strong>{credentialInfo.facilitator_name}</strong></p>
            )}
          </div>
          <span
            data-testid="fd2gfac-credential-status-badge"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] border ${statusStyles}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" /> {status}
          </span>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Credential Code</p>
            <p className="mt-1 text-slate-900 font-semibold">{credentialInfo.credential_code}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Credential ID</p>
            <p className="mt-1 text-slate-900 font-mono" data-testid="fd2gfac-credential-id">{credentialInfo.credential_id}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Original Issue</p>
            <p className="mt-1 text-slate-900">{fmt(credentialInfo.original_issue_date)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Current Period</p>
            <p className="mt-1 text-slate-900">
              {fmt(credentialInfo.current_period_start)} – {fmt(credentialInfo.valid_through)}
            </p>
          </div>
          <div className="sm:col-span-2">
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Valid Through</p>
            <p className="mt-1 text-slate-900 font-semibold">{fmt(credentialInfo.valid_through)}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-x-4 gap-y-3" data-testid="fd2gfac-credential-actions">
        {credentialInfo?.facilitator_resource_center_access && (
          <Link
            to="/academy/portal/fd2g-fac-001/resource-center"
            data-testid="fd2gfac-open-resource-center-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B]"
          >
            <Sparkles className="w-4 h-4" /> OPEN FACILITATOR RESOURCE CENTER
          </Link>
        )}
        <button
          type="button"
          onClick={onViewCredential}
          data-testid="fd2gfac-view-credential-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-semibold hover:bg-[#3B597B]"
        >
          <Award className="w-4 h-4" /> VIEW CREDENTIAL
        </button>
        <button
          type="button"
          onClick={onDownloadCertificate}
          data-testid="fd2gfac-download-certificate-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:border-[#4A6FA5]"
        >
          <FileText className="w-4 h-4" /> DOWNLOAD CERTIFICATE
        </button>
        <button
          type="button"
          onClick={() => {
            const cid = credentialInfo?.credential_id;
            if (!cid) return;
            const url = `/academy/fd2g-fac-001/verify/${encodeURIComponent(cid)}`;
            // Open the public verification page in a new tab so the learner
            // sees exactly what any third party would see.
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          disabled={!credentialInfo?.credential_id}
          title={credentialInfo?.credential_id ? "Open the public verification page for this credential in a new tab." : undefined}
          data-testid="fd2gfac-verify-credential-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:border-[#4A6FA5] hover:text-[#4A6FA5] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShieldCheck className="w-4 h-4" /> VERIFY CREDENTIAL
        </button>
      </div>
      <p className="text-[11px] italic text-slate-500" data-testid="fd2gfac-verify-note">
        Verify Credential opens the public verification page for this credential in a new tab. Preview credentials are
        intentionally not publicly verifiable.
      </p>

      {/* Authorization card */}
      <div className="rounded-xl border border-slate-200 bg-white px-5 py-5" data-testid="fd2gfac-authorization-card">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4A6FA5]">What Your Certification Means</p>
        <p className="mt-2 text-slate-800 leading-relaxed text-sm">
          While your certification is <strong>ACTIVE</strong> and in good standing, you may use the designation:
        </p>
        <p className="mt-2 font-heading text-lg text-slate-900">Certified From Diagnosis to Goodbye Facilitator</p>
        <p className="mt-3 text-sm text-slate-700 leading-relaxed">
          Your certification authorizes you to facilitate approved From Diagnosis to Goodbye discussions and
          educational experiences subject to applicable program permissions and organizational licensing requirements.
        </p>
        <p className="mt-4 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Your certification does not authorize you to</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">
          <li>certify other facilitators</li>
          <li>issue FD2G credentials</li>
          <li>sell organizational licenses</li>
          <li>reproduce protected materials without authorization</li>
          <li>create an unauthorized FD2G certification program</li>
          <li>represent yourself as the creator or owner of FD2G</li>
          <li>act as an official FD2G spokesperson unless separately authorized</li>
          <li>practice outside your professional qualifications</li>
        </ul>
        <div className="mt-4 rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3">
          <p className="text-slate-800 italic">
            "Your certification belongs to you. Organizational licensing belongs to the organization."
          </p>
        </div>
      </div>

    </div>
  );
}


// ---------------------------------------------------------------------------
// ViewCredentialModal
// ---------------------------------------------------------------------------
// Read-only credential viewer. The record passed in comes directly from the
// server GET /credential call and is the authoritative source. This modal
// never issues, re-issues, or alters the credential — it just displays the
// server-returned fields.
// ---------------------------------------------------------------------------
function ViewCredentialModal({ open, loading, record, onClose, onDownloadCertificate }) {
  if (!open) return null;

  const fmt = (iso) => {
    if (!iso) return "\u2014";
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch (_) {
      return iso;
    }
  };

  const status = record?.status || "ACTIVE";
  const statusStyles = status === "ACTIVE"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === "EXPIRING SOON"
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-rose-50 text-rose-800 border-rose-200";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fd2gfac-view-credential-title"
      data-testid="fd2gfac-view-credential-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          data-testid="fd2gfac-view-credential-close-btn"
          aria-label="Close credential view"
          className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800"
        >
          <X className="w-5 h-5" />
        </button>

        {loading && !record && (
          <div className="px-6 py-12 text-center text-slate-500" data-testid="fd2gfac-view-credential-loading">
            Loading credential record…
          </div>
        )}

        {record && (
          <div className="px-6 py-6 sm:px-8 sm:py-8">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7238]">Credential Record</p>
            <h2
              id="fd2gfac-view-credential-title"
              className="mt-1 font-heading text-2xl md:text-3xl text-slate-900"
            >
              {record.credential_name || "Certified From Diagnosis to Goodbye Facilitator"}
            </h2>
            {record.facilitator_name && (
              <p className="mt-2 text-sm text-slate-700">
                Issued to <strong data-testid="fd2gfac-view-credential-name">{record.facilitator_name}</strong>
              </p>
            )}

            <div className="mt-5">
              <span
                data-testid="fd2gfac-view-credential-status"
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] border ${statusStyles}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current" /> {status}
              </span>
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Credential Code</p>
                <p className="mt-1 text-slate-900 font-semibold">{record.credential_code}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Credential ID</p>
                <p
                  className="mt-1 text-slate-900 font-mono"
                  data-testid="fd2gfac-view-credential-id"
                >
                  {record.credential_id}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Original Issue</p>
                <p className="mt-1 text-slate-900" data-testid="fd2gfac-view-credential-original-issue">
                  {fmt(record.original_issue_date)}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Current Period</p>
                <p className="mt-1 text-slate-900">
                  {fmt(record.current_period_start)} – {fmt(record.valid_through)}
                </p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">Valid Through</p>
                <p
                  className="mt-1 text-slate-900 font-semibold"
                  data-testid="fd2gfac-view-credential-valid-through"
                >
                  {fmt(record.valid_through)}
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-lg bg-[#F0F4F8] border-l-4 border-[#4A6FA5] px-4 py-3 text-sm text-slate-700 leading-relaxed">
              This credential record is the authoritative server-side record of your certification. It confirms
              your authorization to use the designation <strong>Certified From Diagnosis to Goodbye Facilitator</strong>{" "}
              while the credential status remains ACTIVE and in good standing. Suspension, expiration, or revocation
              will update this record and the facilitator directory.
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => { onDownloadCertificate(); }}
                data-testid="fd2gfac-view-credential-download-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:border-[#4A6FA5]"
              >
                <FileText className="w-4 h-4" /> Download certificate PDF
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="fd2gfac-view-credential-done-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#1E293B]"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
